
import Vue from 'vue';
import mixins from 'vue-typed-mixins';
import moment from 'moment';
import { mapGetters } from 'vuex';
import { restApi } from '@/components/mixins/restApi';
import Modal from '@/components/Modal.vue';
import ModalButtonSuccess from '@/components/ModalButtonSuccess.vue';
import ModalButtonCancel from '@/components/ModalButtonCancel.vue';
import AlertNotificationModal from '@/components/AlertNotificationModal.vue';
import { N8nSpinner } from 'n8n-design-system';
import { ACCESS_VERSIONS_MODAL_KEY, DUPLICATE_MODAL_KEY, PUBLISH_VERSION_MODAL_KEY } from '@/constants';

interface Item {
	id: number;
	nome?: string;
	nomePublicacao?: string;
	descricaoPublicacao?: string;
	modificadoPorUsuario: {
		nome?: string;
	};
	modificadoEmDataHora: string;
	publicado?: boolean;
}

interface GroupedItem {
	type: 'version' | 'alteracoes';
	version?: Item;
	alteracoes?: Item[];
	id: number;
}

export default mixins(restApi).extend({
	name: 'AccessVersionsModal',
	components: {
		Modal,
		ModalButtonSuccess,
		ModalButtonCancel,
		AlertNotificationModal,
		N8nSpinner,
	},
	props: {
		dataModal: {
			type: Object,
			default: () => ({}),
		},
	},
	data() {
		return {
			modalBus: new Vue(),
			ACCESS_VERSIONS_MODAL_KEY,
			versionData: [] as Item[],
			isLoading: false,
			openAlteracoes: {} as Record<number, boolean>,
			selectedVersionId: null as number | null,
			showPublishAlert: false,
			versionToRepublish: null as Item | null,
			isPublishingVersion: false,
		};
	},
	computed: {
		...mapGetters({
			workspace: 'workspace',
		}),
		workflowId(): string {
			return this.dataModal.workflowId || this.$route.params.workflowid || this.$route.params.name || this.$store.getters.workflowId || '';
		},
		groupedItems(): GroupedItem[] {
			const sortedData = [...this.versionData].sort((a, b) => {
				return moment(b.modificadoEmDataHora).diff(moment(a.modificadoEmDataHora));
			});

			const result: GroupedItem[] = [];
			let currentAlteracoes: Item[] = [];
			let groupId = 1;

			sortedData.forEach((item, index) => {
				if (item.nomePublicacao) {
					if (currentAlteracoes.length > 0) {
						result.push({
							type: 'alteracoes',
							alteracoes: currentAlteracoes,
							id: groupId++,
						});
						currentAlteracoes = [];
					}

					result.push({
						type: 'version',
						version: item,
						id: item.id,
					});
				} else {
					currentAlteracoes.push(item);

					if (index === sortedData.length - 1 && currentAlteracoes.length > 0) {
						result.push({
							type: 'alteracoes',
							alteracoes: currentAlteracoes,
							id: groupId++,
						});
					}
				}
			});

			return result;
		},
	},
	watch: {
		workflowId() {
			this.loadWorkflowVersions();
		},
	},
	mounted() {
		this.loadWorkflowVersions();
	},
	methods: {
		async loadWorkflowVersions() {
			if (!this.workflowId) {
				this.versionData = [];
				return;
			}

			this.isLoading = true;
			try {
				const response = await this.restApi().getWorkflowVersion(this.workspace.id, this.workflowId);
				this.versionData = response || [];
				if (this.versionData.length > 0 && !this.selectedVersionId) {
					this.selectedVersionId = this.versionData[0].id;
				}
			} catch (error: any) {
				console.error('Erro ao carregar versões:', error);
				this.$store.commit('activeAlert', {
					message: 'Erro ao carregar versões do fluxo',
					status: 'error',
				});
			} finally {
				this.isLoading = false;
			}
		},
		toggleAlteracoes(groupId: number) {
			this.$set(this.openAlteracoes, groupId, !this.openAlteracoes[groupId]);
		},
		isAlteracoesOpen(groupId: number) {
			return this.openAlteracoes[groupId] === true;
		},
		handleMenuAction(command: { action: string; item: Item }) {
			this.handleItemAction(command.action, command.item);
		},
		selectVersion(item: Item) {
			this.selectedVersionId = item.id;
		},
		async handleItemAction(action: string, item: Item) {
			if (action === 'edit') {
				this.$router.push({
					name: 'EditVersionById',
					params: {
						workflowid: this.workflowId,
						versionid: String(item.id),
					},
				});
				this.closeModal();
				return;
			}

			if (action === 'publish') {
				if (item.nomePublicacao) {
					this.versionToRepublish = item;
					this.showPublishAlert = true;
					return;
				}

				this.$store.commit('setVersionToPublish', item.id);
				this.$store.commit('setWorkflowIdToPublish', this.workflowId || null);
				this.closeModal();
				this.$store.dispatch('ui/openModalWithData', {
					modalKey: PUBLISH_VERSION_MODAL_KEY,
					data: { skipRedirectAfterPublish: true },
				});
				return;
			}

			if (action === 'openNewTab') {
				const url = this.$router.resolve({
					name: 'VersionById',
					params: { versionid: String(item.id), workflowid: this.workflowId },
				}).href;
				window.open(url, '_blank');
				return;
			}

			if (action === 'saveCopy') {
				try {
					const version = await this.restApi().getVersionData(this.workspace.id, String(item.id), this.workflowId);
					const data = JSON.parse(version.workflowDataJson);
					this.$store.commit('setWorkflowVersionData', {
						...data,
					});
					this.$store.dispatch('ui/openModal', DUPLICATE_MODAL_KEY);
					this.closeModal();
				} catch (error: any) {
					this.$store.commit('activeAlert', {
						message: 'Erro ao salvar cópia da versão',
						status: 'error',
					});
				}
			}
		},
		async confirmPublishVersion() {
			if (!this.versionToRepublish || !this.versionToRepublish.nomePublicacao) {
				this.cancelPublishVersion();
				return;
			}

			const versionToRepublish = this.versionToRepublish;
			this.isPublishingVersion = true;

			try {
				await this.restApi().publishWorkflowVersion(
					this.workspace.id,
					this.workflowId,
					String(versionToRepublish.id),
					{
						nomeVersao: versionToRepublish.nomePublicacao,
						descricaoVersao: versionToRepublish.descricaoPublicacao || '',
					},
				);

				this.$store.commit('activeAlert', {
					message: 'Versão publicada com sucesso',
				});
				this.showPublishAlert = false;
				this.versionToRepublish = null;
				await this.loadWorkflowVersions();
			} catch (error: any) {
				this.$store.commit('activeAlert', {
					message: 'Erro ao publicar versão: ' + (error && error.notifications),
					status: 'error',
				});
			} finally {
				this.isPublishingVersion = false;
			}
		},
		cancelPublishVersion() {
			this.showPublishAlert = false;
			this.versionToRepublish = null;
		},
		confirmAccessVersion() {
			if (!this.selectedVersionId) {
				return;
			}

			this.$router.push({
				name: 'VersionById',
				params: {
					workflowid: this.workflowId,
					versionid: String(this.selectedVersionId),
				},
			});
			this.closeModal();
		},
		formatDate(date: string) {
			if (!date) return '';
			const dateUtc = moment.utc(date);
			return moment(dateUtc).local().format('DD/MM/YYYY [às] HH[h]:mm[min]');
		},
		closeModal() {
			this.modalBus.$emit('close');
		},
	},
});
